import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  FileText, AlertTriangle, CheckCircle2, Clock, DollarSign,
  Plus, ExternalLink, Trash2, ChevronDown, ChevronRight, Building,
  Calendar, RefreshCw, Save, Mail, Copy, X, MessageSquare, Download,
} from "lucide-react";

const CAD = (v) => `${(Number(v) || 0).toFixed(2)} $`;

// Construit un message-type de négociation/relance personnalisé pour un créancier.
function buildCreditorMessages(f) {
  const supplier = f.supplier_name || "cher partenaire";
  const remaining = (f.total_amount || 0) - (f.amount_paid || 0);
  const plan = f.repayment_plan;
  const dojo = "Impact Kick-Boxing inc. (réseau Kaizen)";
  const interac = "impactkb.bjj@gmail.com (réponse : « impact »)";

  let planLine = "";
  let planSms = "";
  if (plan && plan.monthly_amount) {
    planLine = `Je vous propose un arrangement de paiement de ${CAD(plan.monthly_amount)} par mois, de ${plan.start_month} à ${plan.end_month}, par virement Interac.`;
    planSms = ` Proposition : ${CAD(plan.monthly_amount)}/mois (${plan.start_month}→${plan.end_month}) par Interac.`;
  } else {
    planLine = "J'aimerais convenir avec vous d'un échéancier de paiement réaliste. Dites-moi le montant mensuel qui vous conviendrait.";
    planSms = " J'aimerais convenir d'un échéancier de paiement avec vous.";
  }

  const soldeTxt = remaining > 0 ? `un solde de ${CAD(remaining)}` : "notre solde";

  const email =
`Objet : Gala Gladiateur du Nord — arrangement de paiement (${supplier})

Bonjour,

Je vous remercie sincèrement pour votre collaboration lors du Gala Gladiateur du Nord. Votre travail a grandement contribué au succès de l'événement.

Je vous écris au sujet de ${soldeTxt}${f.invoice_number ? ` (réf. #${f.invoice_number})` : ""}. Je tiens à honorer cet engagement et à rester transparent avec vous.

${planLine}

Le paiement se fera par virement Interac à l'adresse suivante :
${interac}

Merci de me confirmer si cet arrangement vous convient, ou de me proposer un ajustement. Je reste entièrement disponible.

Au plaisir,
Ralph David Jean-Pierre
${dojo}`;

  const sms =
`Bonjour, ici Ralph (Institut Impact / Gala Gladiateur du Nord). Merci encore pour votre collaboration. Concernant ${soldeTxt}${f.invoice_number ? ` (#${f.invoice_number})` : ""} :${planSms} Paiement par Interac (${interac}). Est-ce que ça vous convient ? Merci !`;

  return { email, sms };
}

const STATUS = {
  to_pay: { label: "À payer", color: "bg-red-500/20 text-red-300 border-red-500/40", icon: AlertTriangle },
  arrangement_pending: { label: "Arrangement à voir", color: "bg-amber-500/20 text-amber-300 border-amber-500/40", icon: Clock },
  arrangement_active: { label: "Arrangement actif", color: "bg-blue-500/20 text-blue-300 border-blue-500/40", icon: RefreshCw },
  partially_paid: { label: "Paiement partiel", color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40", icon: DollarSign },
  paid: { label: "Payée ✓", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", icon: CheckCircle2 },
  disputed: { label: "Contestée", color: "bg-purple-500/20 text-purple-300 border-purple-500/40", icon: AlertTriangle },
};

const REMINDER_STATUS = {
  sent: { label: "Relancé", color: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
  awaiting_reply: { label: "En attente de réponse", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  agreed: { label: "Arrangement accepté", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  refused: { label: "Refusé", color: "bg-red-500/20 text-red-300 border-red-500/40" },
};

export default function FacturesImpayeesModule() {
  const [factures, setFactures] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [newF, setNewF] = useState({});
  const [payAmount, setPayAmount] = useState({});
  const [payMethod, setPayMethod] = useState({});
  const [payNote, setPayNote] = useState({});
  const [statusDraft, setStatusDraft] = useState({});
  const [notesDraft, setNotesDraft] = useState({});
  const [emailDraft, setEmailDraft] = useState({});
  const [msgFacture, setMsgFacture] = useState(null);

  const load = useCallback(async () => {
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const [l, s] = await Promise.all([
        api.get(`/factures-impayees${q}`),
        api.get("/factures-impayees/stats"),
      ]);
      setFactures(l.data);
      setStats(s.data);
    } catch { toast.error("Erreur chargement"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const addPayment = async (id) => {
    const amount = parseFloat(payAmount[id] || 0);
    if (amount <= 0) return toast.error("Montant invalide");
    try {
      const r = await api.post(`/factures-impayees/${id}/payment`, {
        amount, method: payMethod[id] || "virement", note: payNote[id] || "",
      });
      toast.success(`Paiement de ${amount} $ enregistré${r.data?.receipt_emailed ? " · reçu envoyé par courriel ✓" : ""}`);
      setPayAmount({ ...payAmount, [id]: "" });
      setPayNote({ ...payNote, [id]: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveCreditorEmail = async (id) => {
    try {
      await api.put(`/factures-impayees/${id}`, { creditor_email: (emailDraft[id] || "").trim() });
      toast.success("Courriel du créancier enregistré");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const updateStatus = async (id) => {
    try {
      await api.put(`/factures-impayees/${id}`, {
        status: statusDraft[id], notes: notesDraft[id]
      });
      toast.success("Mis à jour");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const deleteFacture = async (id) => {
    if (!window.confirm("Supprimer cette facture?")) return;
    try {
      await api.delete(`/factures-impayees/${id}`);
      toast.success("Supprimée");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const createFacture = async () => {
    if (!newF.supplier_name || !newF.invoice_number || !newF.total_amount) {
      return toast.error("Fournisseur, numéro et montant requis");
    }
    try {
      await api.post("/factures-impayees", {
        ...newF,
        total_amount: parseFloat(newF.total_amount),
        status: newF.status || "to_pay",
      });
      toast.success("Facture créée");
      setNewF({});
      setShowCreate(false);
      await load();
    } catch { toast.error("Erreur création"); }
  };

  const seedGala = async () => {
    if (!window.confirm("Importer les dettes du gala Gladiateur du Nord (Top Ring, médecin, vidéo, éclairagiste, Adama…) avec leurs plans de remboursement mensuels ?")) return;
    try {
      const r = await api.post("/factures-impayees/seed-gala");
      toast.success(`${r.data.created} dette(s) importée(s) · ${r.data.skipped} déjà présente(s).`);
      await load();
    } catch { toast.error("Import impossible"); }
  };

  const downloadReceipt = async (factureId, paymentId) => {
    try {
      const r = await api.get(`/factures-impayees/${factureId}/payment/${paymentId}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossible de générer le reçu"); }
  };

  const logReminder = async (id, status, channel) => {
    try {
      await api.post(`/factures-impayees/${id}/log-reminder`, { status, channel });
      toast.success("Relance enregistrée");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const totalDue = stats?.total_amount_due_now || 0;
  const totalOutstanding = stats?.total_outstanding || 0;
  const byStatus = stats?.by_status || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="factures-impayees-module">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            Factures à payer
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Suivi des factures fournisseurs · arrangements · paiements partiels
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(!showCreate)} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nouvelle facture
          </button>
          <button onClick={seedGala} data-testid="seed-gala-btn" className="px-4 py-2 rounded-lg border border-red-600 bg-red-900/20 hover:bg-red-900/40 text-red-200 text-sm font-semibold flex items-center gap-2">
            🥊 Importer dettes du gala
          </button>
          <button onClick={load} className="px-4 py-2 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total dû maintenant" value={`${totalDue.toFixed(2)} $`} color="text-red-300" icon={AlertTriangle} pulse={totalDue > 0} />
        <StatCard label="À payer" value={byStatus.to_pay?.count || 0} color="text-red-300" icon={AlertTriangle} />
        <StatCard label="Arrangement" value={(byStatus.arrangement_pending?.count || 0) + (byStatus.arrangement_active?.count || 0)} color="text-amber-300" icon={Clock} />
        <StatCard label="Payées" value={byStatus.paid?.count || 0} color="text-emerald-300" icon={CheckCircle2} />
      </div>

      {/* CREATE FORM */}
      {showCreate && (
        <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="create-form">
          <h3 className="text-sm font-bold text-white mb-3">Nouvelle facture à payer</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="new-supplier" placeholder="Fournisseur *" value={newF.supplier_name || ""}
                   onChange={e => setNewF({ ...newF, supplier_name: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <input data-testid="new-invoice-num" placeholder="N° de facture *" value={newF.invoice_number || ""}
                   onChange={e => setNewF({ ...newF, invoice_number: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <input data-testid="new-amount" type="number" step="0.01" placeholder="Montant total $ *" value={newF.total_amount || ""}
                   onChange={e => setNewF({ ...newF, total_amount: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <input data-testid="new-due-date" type="date" placeholder="Date d'échéance" value={newF.due_date || ""}
                   onChange={e => setNewF({ ...newF, due_date: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <input data-testid="new-category" placeholder="Catégorie (ex: gala, dojo)" value={newF.category || ""}
                   onChange={e => setNewF({ ...newF, category: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <input data-testid="new-creditor-email" type="email" placeholder="Courriel créancier (reçu auto)" value={newF.creditor_email || ""}
                   onChange={e => setNewF({ ...newF, creditor_email: e.target.value })}
                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
            <select data-testid="new-status" value={newF.status || "to_pay"}
                    onChange={e => setNewF({ ...newF, status: e.target.value })}
                    className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <textarea data-testid="new-notes" rows={2} placeholder="Notes (arrangement, contexte)" value={newF.notes || ""}
                    onChange={e => setNewF({ ...newF, notes: e.target.value })}
                    className="mt-3 w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
          <div className="mt-3 flex gap-2">
            <button data-testid="save-new-facture" onClick={createFacture} className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold">
              Enregistrer
            </button>
            <button onClick={() => { setShowCreate(false); setNewF({}); }} className="px-4 py-2 rounded bg-zinc-800 text-zinc-300 text-sm">Annuler</button>
          </div>
        </div>
      )}

      {/* FILTRES */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded text-xs ${filter === "all" ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400"}`}>
          Toutes
        </button>
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded text-xs ${filter === k ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* LISTE */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50">
        {factures.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Aucune facture {filter !== "all" ? "dans ce statut" : ""}.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {factures.map(f => {
              const sc = STATUS[f.status] || STATUS.to_pay;
              const Icon = sc.icon;
              const isOpen = expanded === f.id;
              const remaining = f.total_amount - (f.amount_paid || 0);
              const pct = f.total_amount > 0 ? ((f.amount_paid || 0) / f.total_amount * 100) : 0;
              return (
                <div key={f.id} className="p-4 hover:bg-zinc-900/40" data-testid={`facture-${f.id}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => setExpanded(isOpen ? null : f.id)} className="mt-1 text-zinc-500 hover:text-white">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <Building className="w-4 h-4 mt-1 text-zinc-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="font-semibold text-white">{f.supplier_name}</span>
                        <span className="text-xs text-zinc-500">#{f.invoice_number}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${sc.color} flex items-center gap-1`}>
                          <Icon className="w-3 h-3" /> {sc.label}
                        </span>
                        {f.category && <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">{f.category}</span>}
                        {f.priority === "high" && <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">URGENT</span>}
                        {f.reminder_status && REMINDER_STATUS[f.reminder_status] && (
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${REMINDER_STATUS[f.reminder_status].color}`} data-testid={`reminder-badge-${f.id}`}>
                            ✉️ {REMINDER_STATUS[f.reminder_status].label}{f.last_reminder_at ? ` · ${f.last_reminder_at.slice(0, 10)}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                        <span>Total <strong className="text-white">{f.total_amount.toFixed(2)} $</strong></span>
                        {f.amount_paid > 0 && <span>Payé : {f.amount_paid.toFixed(2)} $</span>}
                        {remaining > 0 && <span className="text-red-300">Reste : {remaining.toFixed(2)} $</span>}
                        {f.invoice_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {f.invoice_date.slice(0, 10)}</span>}
                      </div>
                      {f.repayment_plan && (
                        <div className="mt-1.5 text-[11px] text-cyan-300 flex items-center gap-1" data-testid={`plan-${f.id}`}>
                          📅 Plan : <strong>{f.repayment_plan.monthly_amount?.toFixed(2)} $/mois</strong>
                          <span className="text-zinc-500">· {f.repayment_plan.start_month} → {f.repayment_plan.end_month}</span>
                        </div>
                      )}
                      {f.amount_paid > 0 && (
                        <div className="mt-2 w-full max-w-md bg-zinc-800 rounded-full h-1.5">
                          <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setMsgFacture(f)}
                      data-testid={`msg-btn-${f.id}`}
                      title="Message-type de relance / négociation"
                      className="mt-0.5 shrink-0 px-2.5 py-1.5 rounded-lg border border-cyan-600/40 bg-cyan-900/20 hover:bg-cyan-900/40 text-cyan-200 text-xs font-semibold flex items-center gap-1.5"
                    >
                      <Mail className="w-3.5 h-3.5" /> Message-type
                    </button>
                  </div>

                  {isOpen && (
                    <div className="mt-4 ml-7 space-y-4">
                      {f.description && (
                        <div className="p-3 rounded bg-zinc-900 text-xs text-zinc-300">
                          <div className="text-zinc-500 mb-1">Description</div>
                          {f.description}
                        </div>
                      )}
                      {f.notes && (
                        <div className="p-3 rounded bg-amber-500/5 border border-amber-500/20 text-xs text-zinc-200">
                          <div className="text-amber-300 mb-1">Notes</div>
                          {f.notes}
                        </div>
                      )}
                      {f.pdf_url && (
                        <a href={f.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200">
                          <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le PDF
                        </a>
                      )}

                      {/* COURRIEL DU CRÉANCIER (pour envoi auto du reçu) */}
                      <div className="p-3 rounded bg-zinc-900">
                        <div className="text-xs text-zinc-500 mb-2 font-semibold flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> Courriel du créancier
                          <span className="text-zinc-600 font-normal">(reçu envoyé automatiquement à chaque paiement)</span>
                        </div>
                        <div className="flex gap-2">
                          <input
                            data-testid={`creditor-email-${f.id}`}
                            type="email"
                            placeholder="courriel@exemple.com"
                            value={emailDraft[f.id] !== undefined ? emailDraft[f.id] : (f.creditor_email || "")}
                            onChange={e => setEmailDraft({ ...emailDraft, [f.id]: e.target.value })}
                            className="flex-1 px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
                          />
                          <button data-testid={`save-email-${f.id}`} onClick={() => saveCreditorEmail(f.id)}
                            className="px-3 py-2 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold flex items-center gap-1">
                            <Save className="w-3.5 h-3.5" /> Enregistrer
                          </button>
                        </div>
                      </div>

                      {/* JOURNAL DES RELANCES */}
                      {f.reminders && f.reminders.length > 0 && (
                        <div className="p-3 rounded bg-sky-500/5 border border-sky-500/20" data-testid={`reminders-journal-${f.id}`}>
                          <div className="text-xs text-sky-300 mb-2 font-semibold">Journal des relances ({f.reminders.length})</div>
                          <div className="space-y-1.5">
                            {[...f.reminders].reverse().map(rem => (
                              <div key={rem.id} className="text-xs text-zinc-300 flex flex-wrap items-center gap-2">
                                <span className="text-zinc-500 font-mono">{(rem.date || "").slice(0, 10)}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded border ${(REMINDER_STATUS[rem.status] || {}).color || "border-zinc-700 text-zinc-400"}`}>
                                  {(REMINDER_STATUS[rem.status] || {}).label || rem.status}
                                </span>
                                <span className="text-zinc-500">· {rem.channel}</span>
                                {rem.note && <span className="text-zinc-400 italic">— {rem.note}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AJOUTER PAIEMENT */}
                      {f.status !== "paid" && (
                        <div className="p-3 rounded bg-emerald-500/5 border border-emerald-500/20">
                          <div className="text-xs text-emerald-300 mb-2 font-semibold">Enregistrer un paiement</div>
                          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                            <input data-testid={`pay-amount-${f.id}`} type="number" step="0.01" placeholder="Montant $"
                                   value={payAmount[f.id] || ""}
                                   onChange={e => setPayAmount({ ...payAmount, [f.id]: e.target.value })}
                                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
                            <select data-testid={`pay-method-${f.id}`} value={payMethod[f.id] || "virement"}
                                    onChange={e => setPayMethod({ ...payMethod, [f.id]: e.target.value })}
                                    className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white">
                              <option value="virement">Virement</option>
                              <option value="cheque">Chèque</option>
                              <option value="cash">Cash</option>
                              <option value="carte">Carte</option>
                            </select>
                            <input data-testid={`pay-note-${f.id}`} placeholder="Note"
                                   value={payNote[f.id] || ""}
                                   onChange={e => setPayNote({ ...payNote, [f.id]: e.target.value })}
                                   className="px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
                            <button data-testid={`add-payment-${f.id}`} onClick={() => addPayment(f.id)} className="px-3 py-2 rounded bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold">
                              + Paiement
                            </button>
                          </div>
                        </div>
                      )}

                      {/* HISTORIQUE PAIEMENTS */}
                      {f.payments && f.payments.length > 0 && (
                        <div className="p-3 rounded bg-zinc-900">
                          <div className="text-xs text-zinc-500 mb-2 font-semibold">Historique des paiements</div>
                          <div className="space-y-1">
                            {f.payments.map(p => (
                              <div key={p.id} className="text-xs text-zinc-300 flex items-center gap-2">
                                <span className="text-emerald-400">+ {p.amount.toFixed(2)} $</span>
                                <span className="text-zinc-500">{p.method}</span>
                                <span className="text-zinc-500">·</span>
                                <span className="text-zinc-500">{p.date.slice(0, 10)}</span>
                                {p.note && <span className="text-zinc-400 italic">— {p.note}</span>}
                                <button
                                  data-testid={`receipt-btn-${p.id}`}
                                  onClick={() => downloadReceipt(f.id, p.id)}
                                  title="Télécharger le reçu PDF"
                                  className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-cyan-600/40 bg-cyan-900/20 hover:bg-cyan-900/40 text-cyan-200 text-[11px]"
                                >
                                  <Download className="w-3 h-3" /> Reçu PDF
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CHANGER STATUT + NOTES */}
                      <div className="p-3 rounded bg-zinc-900">
                        <div className="text-xs text-zinc-500 mb-2 font-semibold">Mise à jour</div>
                        <div className="flex gap-2 mb-2">
                          <select data-testid={`status-${f.id}`} value={statusDraft[f.id] || f.status}
                                  onChange={e => setStatusDraft({ ...statusDraft, [f.id]: e.target.value })}
                                  className="flex-1 px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white">
                            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          <button data-testid={`save-status-${f.id}`} onClick={() => updateStatus(f.id)} className="px-3 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold flex items-center gap-1">
                            <Save className="w-3.5 h-3.5" /> Sauver
                          </button>
                        </div>
                        <textarea data-testid={`notes-${f.id}`} rows={2} placeholder="Mettre à jour les notes (arrangement, calculs caisse, etc.)"
                                  value={notesDraft[f.id] !== undefined ? notesDraft[f.id] : (f.notes || "")}
                                  onChange={e => setNotesDraft({ ...notesDraft, [f.id]: e.target.value })}
                                  className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
                      </div>

                      <button data-testid={`delete-${f.id}`} onClick={() => deleteFacture(f.id)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Supprimer la facture
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {msgFacture && (
        <CreditorMessageModal
          facture={msgFacture}
          onClose={() => setMsgFacture(null)}
          onLogReminder={(status, channel) => logReminder(msgFacture.id, status, channel)}
        />
      )}
    </div>
  );
}

function CreditorMessageModal({ facture, onClose, onLogReminder }) {
  const { email, sms } = buildCreditorMessages(facture);
  const [tab, setTab] = useState("email");
  const [reminderStatus, setReminderStatus] = useState(facture.reminder_status || "sent");
  const text = tab === "email" ? email : sms;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(tab === "email" ? "Courriel copié ✓" : "SMS copié ✓");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const markSent = () => {
    onLogReminder(reminderStatus, tab === "email" ? "courriel" : "sms");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="msg-modal" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-zinc-700 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-300" /> Message-type — {facture.supplier_name}
            </h3>
            <p className="text-xs text-zinc-500 mt-1">Pré-rédigé et personnalisé (solde, plan mensuel, Interac). Copie-le puis envoie-le toi-même.</p>
          </div>
          <button onClick={onClose} data-testid="msg-close" className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 mb-3">
          <button data-testid="msg-tab-email" onClick={() => setTab("email")}
            className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 ${tab === "email" ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400"}`}>
            <Mail className="w-3.5 h-3.5" /> Courriel
          </button>
          <button data-testid="msg-tab-sms" onClick={() => setTab("sms")}
            className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 ${tab === "sms" ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400"}`}>
            <MessageSquare className="w-3.5 h-3.5" /> SMS
          </button>
        </div>

        <textarea
          data-testid="msg-text"
          readOnly
          value={text}
          rows={tab === "email" ? 16 : 6}
          className="w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 text-sm text-zinc-100 font-mono resize-none"
        />

        <div className="mt-3 flex flex-wrap gap-2 items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">Après envoi :</span>
            <select
              data-testid="reminder-status-select"
              value={reminderStatus}
              onChange={(e) => setReminderStatus(e.target.value)}
              className="px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs"
            >
              {Object.entries(REMINDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button data-testid="mark-reminder-btn" onClick={markSent}
              className="px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold">
              ✓ Marquer relance
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded bg-zinc-800 text-zinc-300 text-sm">Fermer</button>
            <button data-testid="msg-copy-btn" onClick={copy} className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold flex items-center gap-2">
              <Copy className="w-4 h-4" /> Copier le {tab === "email" ? "courriel" : "SMS"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, pulse }) {
  return (
    <div className={`p-4 rounded-lg border ${pulse ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-zinc-500">{label}</div>
          <div className={`text-2xl font-bold ${color} mt-1`}>{value}</div>
        </div>
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
    </div>
  );
}
